/**
 * Shared `apiFetch` wrapper.
 *
 * Responsibilities:
 *   - Inject the `X-Active-Org` header from the org-context cookie so the
 *     backend's attachActiveOrg middleware can scope authz checks to the
 *     user's currently-selected org.
 *   - `credentials: 'include'` so the `tile_session` cookie rides along.
 *   - Throw on non-2xx with a typed `ApiError` that carries the parsed
 *     body, the status code, and the URL.
 *
 * The active-org cookie is written by the Redux orgs slice on every
 * switch. Reading it here (instead of subscribing to the store) keeps
 * this module free of a Redux dependency — same reason it's plain fetch
 * rather than going through a generated client.
 */

export class ApiError extends Error {
  status: number;
  body: unknown;
  url: string;
  constructor(message: string, status: number, body: unknown, url: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.url = url;
  }
}

const ACTIVE_ORG_COOKIE = 'activeOrg';

export function readActiveOrgFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${ACTIVE_ORG_COOKIE}=([^;]+)`));
  return m ? decodeURIComponent(m[1]) : null;
}

export interface ApiFetchInit {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  /** Override the active-org for one specific call (e.g. listing my orgs
   *  before any org is selected). Pass null to skip the header entirely. */
  activeOrgOverride?: string | null;
  /** Skip the JSON parsing path — used for `void` returns (DELETE). */
  raw?: boolean;
}

export async function apiFetch<T = unknown>(path: string, init: ApiFetchInit = {}): Promise<T> {
  const url = path.startsWith('http') ? path : path;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers ?? {}),
  };
  const activeOrg =
    init.activeOrgOverride === null ? null : init.activeOrgOverride ?? readActiveOrgFromCookie();
  if (activeOrg) headers['X-Active-Org'] = activeOrg;

  const res = await fetch(url, {
    method: init.method ?? 'GET',
    credentials: 'include',
    headers,
    body:
      init.body === undefined
        ? undefined
        : typeof init.body === 'string'
        ? init.body
        : JSON.stringify(init.body),
  });

  if (res.status === 204) return undefined as unknown as T;

  const text = await res.text();
  let body: unknown = text;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    /* keep as text */
  }

  if (!res.ok) {
    const msg =
      (body &&
      typeof body === 'object' &&
      'error' in body &&
      typeof (body as any).error === 'string'
        ? (body as any).error
        : `${res.status} ${res.statusText}`) || 'API error';
    // Broadcast authz denials so a global listener can surface a toast.
    // Silent 403s are a big UX footgun — every place a call fails should
    // at minimum say "you are not authorized to do this".
    if (res.status === 403 && typeof window !== 'undefined') {
      const detail = {
        url,
        action: (body as any)?.action,
        resource: (body as any)?.resource,
        reason: (body as any)?.reason,
        message: msg,
      };
      window.dispatchEvent(new CustomEvent('tile:authz-forbidden', { detail }));
    }
    throw new ApiError(msg, res.status, body, url);
  }
  return body as T;
}

/**
 * Atomically write/clear the active-org cookie. Components that switch
 * orgs MUST call this so subsequent `apiFetch`es carry the new header.
 *
 * Path=/ + Max-Age 30d. Not HttpOnly — the browser needs to read it.
 * The session cookie (`tile_session`) is what we actually authenticate
 * with; this one is just a client preference, so no security risk.
 */
export function setActiveOrgCookie(slug: string | null): void {
  if (typeof document === 'undefined') return;
  if (!slug) {
    document.cookie = `${ACTIVE_ORG_COOKIE}=; Path=/; Max-Age=0`;
    return;
  }
  document.cookie = `${ACTIVE_ORG_COOKIE}=${encodeURIComponent(slug)}; Path=/; Max-Age=${
    30 * 24 * 3600
  }; SameSite=Lax`;
}
